/**
 * hamburgerMenuStyles.js
 * 
 * Dedicated module for injecting hamburger menu CSS styles
 * Fixed transparency issues for proper state changes
 */

import { debugLog } from './index';

/**
 * Injects hamburger menu styles into the document
 * This is separated so it can be called independently and only once
 * 
 * @param {Object} options - Configuration options
 * @param {boolean} options.force - Force re-injection even if styles exist
 * @param {string} options.styleId - Custom ID for the style element
 */
function injectHamburgerMenuStyles(options = {}) {
  const { force = false, styleId = 'hamburger-menu-styles' } = options;
  
  // Check if styles are already injected
  if (!force && document.querySelector(`#${styleId}`)) {
    debugLog("Hamburger menu styles already injected, skipping");
    return;
  }
  
  // Remove existing styles if forcing re-injection
  if (force) {
    const existingStyle = document.querySelector(`#${styleId}`);
    if (existingStyle) {
      existingStyle.remove();
      debugLog("Removed existing hamburger menu styles for re-injection");
    }
  }
  
  const style = document.createElement('style');
  style.id = styleId;
  style.textContent = getHamburgerMenuCSS();
  document.head.appendChild(style);
  
  debugLog("Hamburger menu styles injected successfully");
}

/**
 * Returns the complete CSS for hamburger menu functionality
 * 
 * @returns {string} CSS styles as a string
 */
function getHamburgerMenuCSS() {
  return `
    /* ======================
       NON-PAGINATED MODE BASE STYLES
       ====================== */
    
    /* Background wrapper - fills the viewport with whitesmoke background */
    .non-paginated-background {
      min-height: 100vh;
      width: 100%;
      background-color: whitesmoke;
      box-sizing: border-box;
    }
    
    /* Content wrapper - handles centering and spacing from hamburger menu */
    .non-paginated-content-wrapper {
      display: flex;
      justify-content: center;
      align-items: flex-start;
      min-height: 100vh;
      /* Float header over the page — padding-top tracks actual header height via CSS var */
      padding: var(--link-card-height, 80px) 20px 0;
      box-sizing: border-box;
    }
    
    .non-paginated-mode {
      max-width: 816px !important;
      width: 100%;
      margin: 0 !important;
      padding: 0 !important; /* Remove padding, add margin to inner elements instead */
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', sans-serif;
      line-height: 1.6;
      color: #333;
      box-sizing: border-box;
      background-color: transparent; /* Let the background wrapper handle the color */
    }
    
    @media (max-width: 900px) {
      .non-paginated-content-wrapper {
        padding: var(--link-card-height, 80px) 10px 0;
      }
      
      .non-paginated-mode {
        max-width: 100% !important;
      }
    }

    @media (max-width: 600px) {
      .non-paginated-content-wrapper {
        padding: var(--link-card-height, 80px) 8px 0;
      }
      
      .non-paginated-mode {
        max-width: 100% !important;
      }
    }
    
    /* ======================
       SMOOTH SCROLLING + SCROLL OFFSET FOR FIXED HEADER
       ====================== */

    /* Smooth scrolling for CSS-triggered hash navigation */
    html {
      scroll-behavior: smooth;
    }

    /* Offset anchor targets so the fixed WebViewHeader doesn't cover them.
       Uses the CSS variable set by WebViewHeader; falls back to 80px. */
    .title-chapter,
    .title-topic,
    a.heading-anchor {
      scroll-margin-top: calc(var(--link-card-nav-height, 60px) + 20px);
    }

    /* ======================
       CHAPTER STYLING FOR NON-PAGINATED MODE
       ====================== */
    
    /* Style chapters like individual pages with proper spacing and shadows */
    .non-paginated-mode .chapter {
      background-color: #fdfdfd;
      margin: 2rem auto;
      padding: 2rem;
      box-shadow: 3px 3px 8px rgba(122, 128, 145, 0.15);
      border: 1px solid rgba(122, 128, 145, 0.15);
      border-radius: 10px;
      box-sizing: border-box;
      max-width: 100%;
    }
    
    /* First chapter gets extra top margin to account for hamburger menu */
    .non-paginated-mode .chapter:first-child {
      margin-top: 4rem;
    }
    
    /* Ensure chapter headings have proper spacing since they're not at page tops */
    .non-paginated-mode .chapter .title-chapter {
      margin-top: 0 !important; /* Remove top margin since chapter container provides it */
      margin-bottom: 2rem !important; /* Ensure good spacing below chapter title */
    }
    
    /* Topic titles within chapters need better spacing too */
    .non-paginated-mode .topic {
      margin-top: 2rem !important; /* Space between topics */
    }
    
    .non-paginated-mode .topic:first-child {
      margin-top: 1rem !important; /* Less space for first topic after chapter title */
    }
    
    /* Adjust mobile spacing */
    @media (max-width: 900px) {
      .non-paginated-mode .chapter {
        margin: 1rem auto;
        padding: 1.5rem 1rem;
      }
      
      .non-paginated-mode .chapter:first-child {
        margin-top: 2rem;
      }
    }
    @media (max-width: 600px) {
      .non-paginated-mode .chapter {
        margin: 1rem auto;
        padding: 1.5rem 0.75rem;
      }
      
      .non-paginated-mode .chapter:first-child {
        margin-top: 2rem;
      }
    }
    
    .non-paginated-mode .Manual {
      max-width: none !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    .non-paginated-mode .Manual > div {
      max-width: none !important;
      width: 100% !important;
      margin: 0 !important;
      padding: 0 !important;
    }
    
    /* Hide non-first page headers and footers in non-paginated mode */
     .non-paginated-mode .Header--not-first {
      display: none !important;
    }
    
    .non-paginated-mode .Footer--not-first {
      display: none !important;
    }
    
    /* Show first page footer at bottom */
    .non-paginated-mode .Footer--first {
      position: static !important;
      margin: 2rem auto 0 auto;
      padding: 1rem;
      background: #fdfdfd;
      box-shadow: 3px 3px 8px rgba(122, 128, 145, 0.15);
      border: 1px solid rgba(122, 128, 145, 0.15);
      border-radius: 10px;
      max-width: 816px;
      box-sizing: border-box;
    }

    /* show default header/footer inside topic-preview */
    .topic-preview .Header--not-first,
    .topic-preview .Footer--not-first {
      display: block !important;  /* or flex if you prefer */
    }
    /* Show page footer at bottom */
    .non-paginated-mode .topic-preview .Footer--not-first {
      position: static !important;
      margin: 2rem auto 0 auto;
      padding: 1rem;
      background: #fdfdfd;
      box-shadow: 3px 3px 8px rgba(122, 128, 145, 0.15);
      border: 1px solid rgba(122, 128, 145, 0.15);
      border-radius: 10px;
      max-width: 816px;
      box-sizing: border-box;
    }

    @media (max-width: 900px) {
      .non-paginated-mode .Footer--first {
        max-width: 90%;
        margin: 1rem auto 0 auto;
      }
    }
    
    /* Cover page styling for non-paginated */
    .non-paginated-mode .cover-component {
      background-color: #fdfdfd;
      margin: 2rem auto;
      padding: 2rem;
      box-shadow: 3px 3px 8px rgba(122, 128, 145, 0.15);
      border: 1px solid rgba(122, 128, 145, 0.15);
      border-radius: 10px;
      box-sizing: border-box;
    }
    
    /* First cover gets extra top margin */
    .non-paginated-mode .cover-component:first-child {
      margin-top: 4rem;
    }
    
    @media (max-width: 900px) {
      .non-paginated-mode .cover-component {
        margin: 1rem auto;
        padding: 1.5rem 1rem;
      }
      
      .non-paginated-mode .cover-component:first-child {
        margin-top: 2rem;
      }
    }

    @media (max-width: 600px) {
      .non-paginated-mode .cover-component {
        margin: 1rem auto;
        padding: 1.5rem 0.75rem;
      }
      
      .non-paginated-mode .cover-component:first-child {
        margin-top: 2rem;
      }
    }
    
    /* Content readability improvements - only for body text */
    .non-paginated-mode p,
    .non-paginated-mode li {
      max-width: 65ch; /* Optimal reading width ~10-12 words per line */
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
    }
    
    /* Headings can be a bit wider but still readable */
    .non-paginated-mode h1, 
    .non-paginated-mode h2, 
    .non-paginated-mode h3,
    .non-paginated-mode h4,
    .non-paginated-mode h5,
    .non-paginated-mode h6 {
      max-width: 80ch;
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
    }
    
    /* Remove page-specific constraints */
    .non-paginated-mode .mx-page-margin,
    .non-paginated-mode .mx-page-margin-max {
      margin-left: 0 !important;
      margin-right: 0 !important;
    }
    
    /* Remove Tailwind content max-width constraints in web view (apply at all screen sizes) */
    .non-paginated-mode .max-w-content,
    .non-paginated-mode .max-w-content-max,
    .non-paginated-mode .max-w-contentmaxmargin {
      max-width: none !important;
    }
    
    /* Remove page breaks */
    .non-paginated-mode .page-break {
      page-break-before: auto !important;
      break-before: auto !important;
    }
    
    /* Ensure images don't overflow */
    .non-paginated-mode img {
      max-width: 100%;
      height: auto;
    }
  
    
    /* half width should be more like 3/4 or full width depending on size */
    @media (max-width: 900px) {
      .non-paginated-mode w-1/2 {
        width: 75%;
      }
    }
    @media (max-width: 600px) {
      .non-paginated-mode w-1/2 {
        width: 100%
      }
    }
    /* ======================
       INTEGRATED HAMBURGER MENU STYLES
       ====================== */
    
    .integrated-hamburger-menu {
      position: fixed;
      top: 12px;
      left: 12px;
      /* FIXED: Solid background instead of transparent */
      background: #ffffff;
      border: 1px solid #e0e0e0;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      z-index: 1000;
      transition: all 0.3s ease;
      border-radius: 8px;
      overflow: hidden;
      /* Collapsed: just fit the hamburger button */
      width: 48px;
    }
    
    .integrated-hamburger-menu.expanded {
      /* Expanded: larger width for better readability */
      width: 280px;
      /* FIXED: Keep solid background when expanded */
      background: #ffffff;
    }
    
    /* Header section - always visible */
    .hamburger-header {
      /* FIXED: Solid blue background instead of transparent */
      background: #0066cc;
      color: white;
      padding: 0;
      display: flex;
      flex-direction: column;
      border-radius: 7px 7px 0 0;
      transition: background 0.3s ease;
    }
    
    .integrated-hamburger-menu.expanded .hamburger-header {
      /* FIXED: Keep same solid background when expanded */
      background: #0066cc;
    }
    
    /* Icon row at the top */
    .hamburger-icon-row {
      display: flex;
      align-items: center;
      justify-content: flex-start;
      padding: 0;
      /* Exact height for hamburger button only */
      height: 48px;
      /* Keep button on the left always */
    }
    
    .hamburger-toggle-btn {
      width: 48px;
      height: 48px;
      background: transparent;
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      flex-shrink: 0;
      border-radius: 7px;
      /* Button stays in same position, no opacity changes */
    }
    
    .hamburger-toggle-btn:hover {
      background: rgba(255, 255, 255, 0.15);
    }
    
    .hamburger-toggle-btn:focus {
      outline: 2px solid rgba(255, 255, 255, 0.5);
      outline-offset: 2px;
    }
    
    /* FIXED: Ensure button doesn't stay highlighted after click */
    .hamburger-toggle-btn:focus:not(:focus-visible) {
      outline: none;
    }
    
    .hamburger-toggle-btn:active {
      background: rgba(255, 255, 255, 0.2);
    }
    
    /* Future action buttons can be added here */
    .header-action-btn {
      width: 48px;
      height: 48px;
      background: transparent;
      color: white;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      transition: all 0.2s ease;
      flex-shrink: 0;
      border-radius: 4px;
    }
    
    .header-action-btn:hover {
      background: rgba(255, 255, 255, 0.15);
    }
    
    .header-action-btn:focus {
      outline: 2px solid rgba(255, 255, 255, 0.5);
      outline-offset: 2px;
    }
    
    /* Title row below icons - completely hidden when collapsed */
    .hamburger-title-row {
      /* FIXED: Solid blue background */
      background: #0066cc;
      transition: all 0.3s ease;
      height: 0;
      opacity: 0;
      overflow: hidden;
      padding: 0;
    }
    
    .integrated-hamburger-menu.expanded .hamburger-title-row {
      height: auto;
      opacity: 1;
      padding: 6px 12px 8px 12px;
      /* FIXED: Keep solid background */
      background: #0066cc;
    }
    
    .menu-title {
      font-size: 0.9rem;
      font-weight: 600;
      white-space: nowrap;
    }
    
    /* Expandable content area */
    .hamburger-expandable-content {
      /* FIXED: Solid white background */
      background: #ffffff;
      max-height: 0;
      overflow: hidden;
      transition: max-height 0.3s ease;
      border-top: 1px solid #e0e0e0;
      border-radius: 0 0 7px 7px;
      /* Add padding to the right for scrollbar spacing */
      padding-right: 8px;
    }
    
    .integrated-hamburger-menu.expanded .hamburger-expandable-content {
      max-height: 60vh;
      overflow-y: auto;
      /* Custom scrollbar styling */
      scrollbar-width: thin;
      scrollbar-color: rgba(0, 102, 204, 0.3) transparent;
    }
    
    /* Webkit scrollbar styling */
    .hamburger-expandable-content::-webkit-scrollbar {
      width: 6px;
    }
    
    .hamburger-expandable-content::-webkit-scrollbar-track {
      background: transparent;
    }
    
    .hamburger-expandable-content::-webkit-scrollbar-thumb {
      background: rgba(0, 102, 204, 0.3);
      border-radius: 3px;
    }
    
    .hamburger-expandable-content::-webkit-scrollbar-thumb:hover {
      background: rgba(0, 102, 204, 0.5);
    }
    
    /* ======================
       TOC LIST STYLES
       ====================== */
    
    .integrated-toc-list {
      list-style: none;
      padding: 0;
      margin: 0;
      width: 280px;
      /* Remove right padding from list since container handles scrollbar spacing */
    }
    
    .toc-chapter {
      border-bottom: 1px solid rgba(240, 240, 240, 0.8);
    }
    
    .toc-chapter:last-child {
      border-bottom: none;
    }
    
    .toc-chapter-toggle {
      display: flex;
      align-items: center;
      padding: 12px 16px;
      /* FIXED: Solid background */
      background: #fafafa;
      cursor: pointer;
      transition: background-color 0.2s ease;
    }
    
    .toc-chapter-toggle:hover {
      /* FIXED: Solid hover background */
      background: #f5f5f5;
    }
    
    .toc-expand-btn {
      background: none;
      border: none;
      cursor: pointer;
      padding: 4px;
      margin-right: 8px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #666;
      transition: all 0.2s ease;
      border-radius: 3px;
      flex-shrink: 0;
    }
    
    .toc-expand-btn:hover {
      /* FIXED: Solid hover background */
      background: #e9ecef;
      color: #333;
    }
    
    .toc-expand-btn:focus {
      outline: 2px solid rgba(0, 102, 204, 0.5);
      outline-offset: 2px;
    }
    
    /* FIXED: Remove focus outline after mouse click */
    .toc-expand-btn:focus:not(:focus-visible) {
      outline: none;
    }
    
    .toc-expand-btn.expanded svg {
      transform: rotate(90deg);
    }
    
    .toc-expand-btn svg {
      transition: transform 0.2s ease;
      width: 16px;
      height: 16px;
    }
    
    .toc-chapter-link {
      flex: 1;
      color: inherit; /* Use Tailwind color classes */
      text-decoration: none;
      /* Remove inline font styles - use Tailwind classes */
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
      cursor: pointer;
      padding: 6px 0;
      transition: color 0.2s ease;
      /* Apply font-size override to match inline TOC */
      font-size: 1em !important;
    }
    
    .toc-chapter-link:hover {
      color: #0066cc;
    }
    
    .toc-chapter-link:focus {
      outline: 2px solid rgba(0, 102, 204, 0.5);
      outline-offset: 2px;
      border-radius: 3px;
    }
    
    /* FIXED: Remove focus outline after mouse click */
    .toc-chapter-link:focus:not(:focus-visible) {
      outline: none;
    }
    
    /* Navigation arrows */
    .toc-nav-arrow {
      background: none;
      border: none;
      cursor: pointer;
      padding: 6px;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #0066cc;
      transition: all 0.2s ease;
      border-radius: 4px;
      flex-shrink: 0;
      text-decoration: none;
      margin-left: 8px;
      margin-right: 4px;
    }
    
    .toc-nav-arrow:hover {
      background: rgba(0, 102, 204, 0.1);
      color: #0052a3;
    }
    
    .toc-nav-arrow:focus {
      outline: 2px solid rgba(0, 102, 204, 0.5);
      outline-offset: 2px;
    }
    
    /* FIXED: Remove focus outline after mouse click */
    .toc-nav-arrow:focus:not(:focus-visible) {
      outline: none;
    }
    
    .toc-nav-arrow svg {
      transition: transform 0.2s ease;
    }
    
    .toc-nav-arrow:hover svg {
      transform: translateX(2px);
    }
    
    .toc-topics {
      list-style: none;
      padding: 0;
      margin: 0;
      /* FIXED: Solid background */
      background: #ffffff;
      /* Remove max-height limit to show all topics */
      max-height: none;
      overflow: visible;
      transition: max-height 0.3s ease;
    }
    
    .toc-topics.collapsed {
      max-height: 0;
      overflow: hidden;
    }
    
    .toc-topic {
      border-bottom: 1px solid rgba(248, 249, 250, 0.8);
    }
    
    .toc-topic:last-child {
      border-bottom: none;
    }
    
    .toc-topic-container {
      display: flex;
      align-items: center;
      padding: 10px 16px 10px 44px;
      transition: all 0.2s ease;
    }
    
    .toc-topic-container:hover {
      /* FIXED: Solid hover background */
      background: #f8f9fa;
    }
    
    .toc-topic-container:hover .toc-topic-link {
      color: #0066cc !important;
    }
    
    .toc-topic-link {
      flex: 1;
      color: inherit; /* Use Tailwind color classes */
      text-decoration: none;
      /* Remove inline font styles - use Tailwind classes */
      word-wrap: break-word;
      overflow-wrap: break-word;
      hyphens: auto;
      transition: color 0.2s ease;
      padding: 2px 0;
      /* Apply font-size override to match inline TOC */
      font-size: 0.8em !important;
    }
    
    .toc-topic-link:focus {
      outline: 2px solid rgba(0, 102, 204, 0.5);
      outline-offset: 2px;
      border-radius: 3px;
    }
    
    /* FIXED: Remove focus outline after mouse click */
    .toc-topic-link:focus:not(:focus-visible) {
      outline: none;
    }
    
    .toc-topic-nav-arrow {
      margin-left: 8px;
      margin-right: 4px;
      padding: 4px;
    }
    
    .toc-topic-nav-arrow svg {
      width: 14px;
      height: 14px;
    }
    
    /* ======================
       RESPONSIVE BEHAVIOR
       ====================== */
    
    /* ======================
       WEB-STATIC CONTEXT: hamburger merges with React header
       ====================== */

    /* Hide the hamburger's own header — the React header replaces it */
    .integrated-hamburger-menu[data-context="web-static"] .hamburger-header,
    .integrated-hamburger-menu[data-context="preview"] .hamburger-header {
      display: none;
    }

    /* Reposition so it opens at the current visible bottom of the header */
    .integrated-hamburger-menu[data-context="web-static"],
    .integrated-hamburger-menu[data-context="preview"] {
      top: var(--link-card-bottom, 120px) !important;
      left: 0 !important;
      border-radius: 0 0 8px 0;
      transition: opacity 0.2s ease;
    }

    /* When collapsed, hide entirely — the React header's toggle btn is the only control */
    .integrated-hamburger-menu[data-context="web-static"]:not(.expanded),
    .integrated-hamburger-menu[data-context="preview"]:not(.expanded) {
      opacity: 0;
      pointer-events: none;
      width: 0;
      overflow: hidden;
      border: none;
      box-shadow: none;
    }

    /* Auto-expand sidebar disabled for now — only shows on toggle */

    @media (min-width: 1200px) {
      /* On large screens, hamburger floats as overlay — content stays centered */
      .non-paginated-content-wrapper {
        padding-left: 20px;
        padding-right: 20px;
        padding-top: var(--link-card-height, 80px);
        justify-content: center;
      }

      .non-paginated-mode {
        max-width: 816px !important;
        margin: 0 auto;
      }
    }

    /* Non-web-static contexts: keep menu expanded at ≥1440px */
    @media (min-width: 1440px) {
      .integrated-hamburger-menu:not([data-context="web-static"]) {
        position: fixed;
        top: 12px;
        left: 12px;
        width: 280px;
      }

      .integrated-hamburger-menu:not([data-context="web-static"]) .hamburger-expandable-content {
        max-height: calc(100vh - 140px);
        overflow-y: auto;
      }

      .integrated-hamburger-menu:not([data-context="web-static"]) .hamburger-title-row {
        height: auto;
        opacity: 1;
        padding: 6px 12px 8px 12px;
      }
    }

    @media (max-width: 1199px) {
      /* On smaller screens, start collapsed */
      .integrated-hamburger-menu {
        width: 48px;
      }

      .integrated-hamburger-menu.expanded {
        width: 280px;
      }
    }
    
    @media (max-width: 768px) {
      /* On mobile, use appropriate sizing - not full screen */
      .integrated-hamburger-menu {
        top: 8px;
        left: 8px;
      }
      
      .integrated-hamburger-menu.expanded {
        width: 300px;
        max-height: 80vh;
      }
      
      .integrated-hamburger-menu.expanded .hamburger-expandable-content {
        max-height: calc(80vh - 100px);
      }
      
      .integrated-toc-list {
        width: 300px;
      }
    }
    
    /* ======================
       BODY SCROLL MANAGEMENT
       ====================== */
    
    /* Prevent body scroll when menu is open on mobile */
    @media (max-width: 768px) {
      body.hamburger-menu-open {
        overflow: hidden;
      }
    }
    
    @media (min-width: 769px) {
      body.hamburger-menu-open {
        overflow: auto;
      }
    }
    
    /* ======================
       SMOOTH SCROLLING & ACCESSIBILITY
       ====================== */
    
    /* Add smooth scrolling and scroll offset for better UX */
    html {
      scroll-behavior: smooth;
    }
    
    /* Smooth padding-top transition on main content when header collapses */
    .non-paginated-background {
      transition: padding-top 0.25s ease-in-out;
    }

    /* Animated collapsible header section — height only; no opacity so bg stays opaque */
    .link-card-collapsible {
      overflow: hidden;
      transition: max-height 0.3s ease-in-out;
    }

    /* Hamburger toggle inside React header — flex sibling (48px, left) */
    .link-card-hamburger-btn {
      width: 48px;
      flex-shrink: 0;
      align-self: stretch;
      background: #0066cc;
      border: none;
      cursor: pointer;
      display: flex;
      align-items: center;
      justify-content: center;
      color: #fff;
    }
    .link-card-hamburger-btn:hover {
      background: #0052a3;
    }

    /* Right spacer — mirrors hamburger width so inner content centers on full viewport */
    .link-card-right-spacer {
      width: 48px;
      flex-shrink: 0;
      background: #f0f4ff;  /* matches BreadcrumbRow background */
    }

    /* Add scroll-margin-top to headings for consistent offset.
       Scoped to non-paginated mode only — paginated /manual/ pages use PagedJS layout
       and should not have this property interfering with paged media rendering. */
    .non-paginated-mode .title-chapter,
    .non-paginated-mode .title-topic,
    .non-paginated-mode .heading-anchor {
      scroll-margin-top: var(--link-card-height, 80px);
    }
    
    /* ======================
       PRINT STYLES
       ====================== */
    
    /* Ensure menu doesn't interfere with print styles */
    @media print {
      .integrated-hamburger-menu {
        display: none !important;
      }

      /* Hide the fixed preview/web-view header chrome and zero out the
         CSS custom properties that pad the page content for it, so print
         output doesn't have a blank strip at the top of every page. */
      .web-view-header-chrome {
        display: none !important;
      }
      :root {
        --link-card-height: 0px !important;
        --link-card-nav-height: 0px !important;
        --link-card-bottom: 0px !important;
      }

      .non-paginated-background {
        background-color: white !important;
      }

      .non-paginated-content-wrapper {
        padding: 0 !important;
        margin: 0 !important;
        justify-content: flex-start !important;
      }

      .non-paginated-mode {
        margin: 0 !important;
        max-width: none !important;
      }
    }
    
    /* ======================
       REDUCED MOTION SUPPORT
       ====================== */
    
    @media (prefers-reduced-motion: reduce) {
      .integrated-hamburger-menu,
      .hamburger-header,
      .hamburger-expandable-content,
      .toc-expand-btn,
      .toc-nav-arrow,
      .toc-topics {
        transition: none;
      }
      
      html {
        scroll-behavior: auto;
      }
      
      .toc-expand-btn svg,
      .toc-nav-arrow svg {
        transition: none;
      }
    }

    /* ======================
       HIGH CONTRAST MODE SUPPORT
       ====================== */

    @media (prefers-contrast: high) {
      .integrated-hamburger-menu {
        border: 2px solid currentColor;
        background: rgba(255, 255, 255, 1);
      }

      .hamburger-header {
        background: rgba(0, 102, 204, 1);
        border-bottom: 2px solid currentColor;
      }

      .toc-nav-arrow:hover,
      .toc-nav-arrow:focus {
        background: rgba(0, 102, 204, 0.3);
      }
    }

    /* ======================
       HOME BUTTON
       ====================== */

    .hamburger-home-btn {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 36px;
      height: 36px;
      margin-left: auto;
      margin-right: 4px;
      color: #fff;
      opacity: 0.85;
      border-radius: 6px;
      text-decoration: none;
      flex-shrink: 0;
      transition: opacity 0.15s, background 0.15s;
    }
    .hamburger-home-btn:hover {
      opacity: 1;
      background: rgba(255,255,255,0.15);
    }

    /* ======================
       EXTRA NAV SECTIONS
       ====================== */

    .menu-section {
      border-top: 1px solid rgba(255,255,255,0.12);
      padding: 0;
    }

    .menu-section-toggle {
      display: flex;
      align-items: center;
      gap: 6px;
      width: 100%;
      padding: 10px 12px;
      background: none;
      border: none;
      color: rgba(255,255,255,0.8);
      font-size: 0.8rem;
      font-weight: 600;
      letter-spacing: 0.06em;
      text-transform: uppercase;
      cursor: pointer;
      text-align: left;
    }
    .menu-section-toggle:hover {
      color: #fff;
      background: rgba(255,255,255,0.07);
    }
    .menu-section-chevron {
      transition: transform 0.2s;
      flex-shrink: 0;
    }
    .menu-section-toggle.expanded .menu-section-chevron {
      transform: rotate(180deg);
    }

    .menu-section-list {
      list-style: none;
      margin: 0;
      padding: 0 0 4px;
      overflow: hidden;
    }
    .menu-section-list.collapsed {
      display: none;
    }

    .menu-section-link {
      display: block;
      padding: 7px 16px 7px 24px;
      color: rgba(255,255,255,0.75);
      text-decoration: none;
      font-size: 0.82rem;
      line-height: 1.4;
      transition: color 0.15s, background 0.15s;
    }
    .menu-section-link:hover {
      color: #fff;
      background: rgba(255,255,255,0.08);
    }

    /* ======================
       LIVE PREVIEW INDICATOR
       ====================== */

    /* Amber dot on the hamburger toggle button — visible even when collapsed */
    [data-preview="true"] .hamburger-toggle-btn {
      position: relative;
    }
    [data-preview="true"] .hamburger-toggle-btn::after {
      content: '';
      position: absolute;
      top: 7px;
      right: 7px;
      width: 8px;
      height: 8px;
      background: #f59e0b;
      border-radius: 50%;
      border: 2px solid #0066cc;
      animation: preview-pulse 2s ease-in-out infinite;
    }
    @keyframes preview-pulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.45; }
    }

    /* Inline dot in the title row (shown when expanded) */
    .preview-dot-inline {
      display: inline-block;
      width: 7px;
      height: 7px;
      background: #f59e0b;
      border-radius: 50%;
      margin-right: 6px;
      vertical-align: middle;
      flex-shrink: 0;
    }

    .preview-mode-title {
      display: flex;
      align-items: center;
      color: #fcd34d;   /* amber-300 — stands out on the blue header */
      letter-spacing: 0.07em;
    }

    /* Exit Preview button in the title row */
    .exit-preview-btn {
      margin-left: auto;
      padding: 2px 8px;
      font-size: 0.7rem;
      font-weight: 600;
      color: #0066cc;
      background: #fcd34d;
      border: none;
      border-radius: 4px;
      cursor: pointer;
      white-space: nowrap;
      flex-shrink: 0;
      transition: background 0.15s;
    }
    .exit-preview-btn:hover {
      background: #fbbf24;
    }

    /* Make the title row a flex row so title + button sit side by side */
    .integrated-hamburger-menu[data-preview="true"] .hamburger-title-row {
      display: flex;
      align-items: center;
      gap: 6px;
    }

    /* ======================
       VIEW MODE ROW (preview mode only)
       Web / PDF / Production toggles — shown when menu is expanded
       ====================== */

    .hamburger-view-row {
      display: none;               /* hidden when collapsed */
      align-items: center;
      gap: 4px;
      padding: 6px 10px 8px;
      background: rgba(0,0,0,0.18);
      border-top: 1px solid rgba(255,255,255,0.1);
    }

    .integrated-hamburger-menu.expanded .hamburger-view-row {
      display: flex;
    }

    .view-mode-btn {
      display: flex;
      align-items: center;
      gap: 4px;
      padding: 4px 8px;
      border-radius: 5px;
      border: 1px solid rgba(255,255,255,0.25);
      color: rgba(255,255,255,0.65);
      text-decoration: none;
      font-size: 0.72rem;
      font-weight: 600;
      letter-spacing: 0.04em;
      cursor: pointer;
      transition: background 0.15s, color 0.15s, border-color 0.15s;
      white-space: nowrap;
    }
    .view-mode-btn:hover {
      background: rgba(255,255,255,0.12);
      color: #fff;
      border-color: rgba(255,255,255,0.5);
    }
    .view-mode-btn.active {
      background: rgba(255,255,255,0.22);
      color: #fff;
      border-color: rgba(255,255,255,0.7);
    }

    /* Production toggle: amber accent when active (production view on) */
    .prod-toggle-btn.active {
      background: rgba(245,158,11,0.25);
      color: #fcd34d;
      border-color: #f59e0b;
    }
    .prod-toggle-btn.active:hover {
      background: rgba(245,158,11,0.35);
    }

    /* Push production toggle to the right */
    .prod-toggle-btn {
      margin-left: auto;
    }
  `;
}

/**
 * Gets base styles for non-paginated mode
 * Can be used independently if only base styles are needed
 * 
 * @returns {string} Base CSS styles
 */
function getNonPaginatedModeCSS() {
  const fullCSS = getHamburgerMenuCSS();
  const startMarker = '/* NON-PAGINATED MODE BASE STYLES */';
  const endMarker = '/* INTEGRATED HAMBURGER MENU STYLES */';
  
  const startIndex = fullCSS.indexOf(startMarker);
  const endIndex = fullCSS.indexOf(endMarker);
  
  if (startIndex === -1 || endIndex === -1) {
    return fullCSS; // Return full CSS if markers not found
  }
  
  return fullCSS.substring(startIndex, endIndex);
}

/**
 * Gets only the hamburger menu styles without base mode styles
 * 
 * @returns {string} Hamburger menu CSS styles only
 */
function getHamburgerMenuOnlyCSS() {
  const fullCSS = getHamburgerMenuCSS();
  const startMarker = '/* INTEGRATED HAMBURGER MENU STYLES */';
  
  const startIndex = fullCSS.indexOf(startMarker);
  
  if (startIndex === -1) {
    return fullCSS; // Return full CSS if marker not found
  }
  
  return fullCSS.substring(startIndex);
}

/**
 * Removes hamburger menu styles from the document
 * 
 * @param {string} styleId - ID of the style element to remove
 */
function removeHamburgerMenuStyles(styleId = 'hamburger-menu-styles') {
  const existingStyle = document.querySelector(`#${styleId}`);
  if (existingStyle) {
    existingStyle.remove();
    debugLog("Hamburger menu styles removed");
  } else {
    debugLog("No hamburger menu styles found to remove");
  }
}

/**
 * Checks if hamburger menu styles are currently injected
 * 
 * @param {string} styleId - ID of the style element to check
 * @returns {boolean} True if styles are injected
 */
function hasHamburgerMenuStyles(styleId = 'hamburger-menu-styles') {
  return !!document.querySelector(`#${styleId}`);
}

export default injectHamburgerMenuStyles;
export {
  getHamburgerMenuCSS,
  getNonPaginatedModeCSS,
  getHamburgerMenuOnlyCSS,
  removeHamburgerMenuStyles,
  hasHamburgerMenuStyles
};